"use client";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Portal } from "@headlessui/react";
import clsx from "clsx";
import { Home, MapPin } from "lucide-react";
import { useState } from "react";
import { usePopper } from "react-popper";
import useAddressAutocomplete from "hooks/useAddressAutocomplete";
import { AddressAnswer } from "components/Quiz/quizTypes";
import { RecordItem } from "types/common";
import { APP_LANGUAGES, APP_STRINGS_LABELS, getAppStringForLanguage } from "utils/language";

const formatAddress = (address?: RecordItem) => {
  if (!address?.addressLine1) return "";

  return `${address.addressLine1}, ${address.city}, ${address.state}`;
};

const AddressAutocomplete = ({
  address,
  updateAddress,
  className,
  hasError,
  language = APP_LANGUAGES.ENGLISH
}: {
  address?: AddressAnswer;
  updateAddress: (newAddress: AddressAnswer) => void;
  className?: string;
  hasError?: boolean;
  language?: APP_LANGUAGES;
}) => {
  const [targetElement, setTargetElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: "bottom-start",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 8]
        }
      }
    ]
  });
  const { suggestions, onChangeInput, onSelect, isRequestError } = useAddressAutocomplete(updateAddress);

  const handleChangeAddress = (value: string) => {
    onSelect(value);
  };

  return (
    <div className={clsx("w-full", className)}>
      <Combobox
        value={formatAddress(address)}
        onChange={handleChangeAddress}
        as="div"
        className="mx-auto flex w-full flex-col px-2 lg:px-0"
      >
        {({ open }) => (
          <div className={clsx("w-full ", open ? "rounded-3xl bg-white " : "rounded-3xl")}>
            <div className="relative" ref={setTargetElement}>
              <ComboboxInput
                onChange={(event) => onChangeInput(event.target.value)}
                as="input"
                className={clsx(
                  " !focus-visible:outline-none placeholder:text-muted-foreground w-full border border-primary-700 py-4 pl-12  pr-7 font-matter outline-none placeholder:font-medium focus:border-transparent focus:ring-2 focus:ring-primary-700 disabled:border-neutral-200 max-sm:text-md",
                  open ? "rounded-xl lg:rounded-xl" : "rounded-xl",
                  hasError && "ring-2 ring-red-700 focus:ring-red-700"
                )}
                placeholder={getAppStringForLanguage(APP_STRINGS_LABELS.ENTER_ADDRESS, language)}
              />
              <MapPin className="text-neutral-800 absolute left-4 top-4 h-6 w-6" />
            </div>
            {hasError && (
              <p className={"mb-1.5 mt-2 block text-start text-base font-medium text-red-700 dark:text-red-dark-700"}>
                {getAppStringForLanguage(APP_STRINGS_LABELS.ADDR_REQUIRED_ERR_MSG, language)}
              </p>
            )}
            <Portal>
              <ComboboxOptions
                className="z-[99999] overflow-hidden rounded-b-md bg-white py-4 shadow-xl"
                as="div"
                ref={(ref) => setPopperElement(ref)}
                style={{ ...styles.popper, width: targetElement?.clientWidth }}
                {...attributes.popper}
              >
                {suggestions.map(({ description, place_id }) => (
                  <ComboboxOption
                    key={place_id}
                    value={place_id}
                    as="div"
                    className="border-1 flex  cursor-pointer items-center gap-x-3 py-3 font-matter text-base hover:bg-[#ddd] ui-active:bg-[#ddd] lg:px-7"
                  >
                    <span className=" flex h-10 w-10 items-center justify-center rounded-full  bg-[#f7f7f7] align-middle">
                      <Home color="#222222" strokeWidth={1.5} />
                    </span>
                    <div className="flex flex-col">
                      <span className="font-matter text-base text-primary-700">{description.replace(", USA", "")}</span>
                    </div>
                  </ComboboxOption>
                ))}
                {!!isRequestError && (
                  <ComboboxOption
                    key={"error_places"}
                    value={undefined}
                    as="div"
                    className="flex cursor-default items-center justify-center gap-x-3 py-3 text-base text-base-secondary  lg:px-7"
                  >
                    <span className="text-base">
                      {getAppStringForLanguage(APP_STRINGS_LABELS.ADDR_NOT_FOUND_ERR_MSG, language)}
                    </span>
                  </ComboboxOption>
                )}
              </ComboboxOptions>
            </Portal>
          </div>
        )}
      </Combobox>
    </div>
  );
};

export default AddressAutocomplete;
